import React, { useEffect } from 'react';
import { useOnboardingStore } from '../../store';
import { SERVICE_TYPES } from '../../types';
import { ArrowLeft, ArrowRight, CheckCircle, Users, Video, Camera, MapPin, Brain, FastForward, Search, BarChart3, X } from 'lucide-react';
import { canSkipSteps } from '../../config/beta';
import { isDeveloperMode } from '../../config/devMode';
import {
  loadServiceSubscriptionAvailability,
  recordServiceAvailabilityInterest,
  serviceIsUnavailable,
  serviceUnavailableMessage,
  type ServiceSubscriptionAvailability,
} from '../../lib/serviceAvailability';

const serviceTypeIcons = {
  'repeat-referral-system': Users,
  'content-engine': Video,
  'proof-of-work': Camera,
  'nap-entity-boost': MapPin,
  'ai-audit': Search,
  'seo-gbp-audit': BarChart3,
  'customer-intelligence': Brain,
};
const REPEAT_REFERRAL_SERVICE_TYPE = 'repeat-referral-system';
const CUSTOMER_INTELLIGENCE_SERVICE_TYPE = 'customer-intelligence';

export function ServiceTypeSelection() {
  const {
    toggleServiceType,
    selectedServiceTypes,
    setSelectedServiceTypes,
    setStep,
    client,
    textAvailabilityNotificationsAllowed,
    setTextAvailabilityNotificationsAllowed,
  } = useOnboardingStore();
  const [availabilityRows, setAvailabilityRows] = React.useState<ServiceSubscriptionAvailability[]>([]);
  const [availabilityState, setAvailabilityState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [availabilityError, setAvailabilityError] = React.useState('');
  const [notifyRequested, setNotifyRequested] = React.useState<Record<string, boolean>>({});
  const [notifyBusy, setNotifyBusy] = React.useState<string | null>(null);

  useEffect(() => {
    let active = true;
    loadServiceSubscriptionAvailability()
      .then((rows) => {
        if (active) {
          setAvailabilityRows(rows);
          setAvailabilityState('ready');
        }
      })
      .catch(() => {
        if (active) {
          setAvailabilityRows([]);
          setAvailabilityState('error');
        }
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (availabilityState !== 'ready' || selectedServiceTypes.length === 0) return;
    const filteredForAvailability = selectedServiceTypes.filter((type) => !serviceIsUnavailable(availabilityRows, type.id));
    const hasCustomerIntelligence = filteredForAvailability.some((type) => type.id === CUSTOMER_INTELLIGENCE_SERVICE_TYPE);
    const hasRepeatReferral = filteredForAvailability.some((type) => type.id === REPEAT_REFERRAL_SERVICE_TYPE);
    const filtered = hasCustomerIntelligence && !hasRepeatReferral
      ? filteredForAvailability.filter((type) => type.id !== CUSTOMER_INTELLIGENCE_SERVICE_TYPE)
      : filteredForAvailability;
    if (filtered.length !== selectedServiceTypes.length) {
      setSelectedServiceTypes(filtered);
    }
  }, [availabilityRows, availabilityState, selectedServiceTypes, setSelectedServiceTypes]);

  useEffect(() => {
    if (isDeveloperMode && selectedServiceTypes.length === 0) {
      const defaultType = SERVICE_TYPES.find(t => t.id === 'content-engine');
      if (defaultType) {
        toggleServiceType(defaultType);
      }
    }
  }, [selectedServiceTypes.length, toggleServiceType]);

  const isTypeSelected = (typeId: string) =>
    selectedServiceTypes.some(type => type.id === typeId);

  const handleAvailableClick = (type: typeof SERVICE_TYPES[number]) => {
    setAvailabilityError('');
    if (availabilityState !== 'ready') {
      setAvailabilityError('Service availability could not be verified. Refresh the page before choosing services.');
      return;
    }
    const selected = isTypeSelected(type.id);
    if (type.id === CUSTOMER_INTELLIGENCE_SERVICE_TYPE && !selected) {
      const repeatReferralType = SERVICE_TYPES.find(serviceType => serviceType.id === REPEAT_REFERRAL_SERVICE_TYPE);
      const withoutCustomerIntelligence = selectedServiceTypes.filter(serviceType => serviceType.id !== CUSTOMER_INTELLIGENCE_SERVICE_TYPE);
      const nextSelection = repeatReferralType && !withoutCustomerIntelligence.some(serviceType => serviceType.id === REPEAT_REFERRAL_SERVICE_TYPE)
        ? [repeatReferralType, ...withoutCustomerIntelligence, type]
        : [...withoutCustomerIntelligence, type];
      setSelectedServiceTypes(nextSelection);
      return;
    }

    if (type.id === REPEAT_REFERRAL_SERVICE_TYPE && selected) {
      setSelectedServiceTypes(selectedServiceTypes.filter(serviceType =>
        serviceType.id !== REPEAT_REFERRAL_SERVICE_TYPE &&
        serviceType.id !== CUSTOMER_INTELLIGENCE_SERVICE_TYPE
      ));
      return;
    }

    toggleServiceType(type);
  };

  const handleUnavailableClick = async (type: typeof SERVICE_TYPES[number]) => {
    setNotifyBusy(type.id);
    try {
      await recordServiceAvailabilityInterest({
        serviceTypeId: type.id,
        serviceName: type.name,
        email: client.email,
        phone: client.phone,
        contactName: client.contactName,
        businessName: client.businessName,
        textNotificationsAllowed: textAvailabilityNotificationsAllowed,
      });
      setNotifyRequested(prev => ({ ...prev, [type.id]: true }));
    } catch {
      setNotifyRequested(prev => ({ ...prev, [type.id]: false }));
      setAvailabilityError('The notification request could not be saved. Please try again.');
    } finally {
      setNotifyBusy(null);
    }
  };

  const handleContinue = () => {
    if (availabilityState !== 'ready') {
      setAvailabilityError('Service availability could not be verified. Refresh the page before continuing.');
      return;
    }
    if (selectedServiceTypes.length === 0) {
      return;
    }
    setStep(3);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSkip = () => {
    if (canSkipSteps()) {
      if (selectedServiceTypes.length === 0) {
        toggleServiceType(SERVICE_TYPES[0]);
      }
      setStep(3);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-white mb-2">
          Choose Your Services
        </h2>
        <p className="text-gray-400">
          Select every service you want included in setup
        </p>
      </div>

      {(availabilityState === 'error' || availabilityError) && (
        <div className="mb-6 rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-200" role="alert">
          {availabilityError || 'Service availability could not be verified. Refresh the page before choosing services.'}
        </div>
      )}

      {client.phone && (
        <label className="mb-6 flex items-start gap-3 rounded-lg border border-gray-700 bg-scale-darker/40 p-4 text-left">
          <input
            type="checkbox"
            checked={textAvailabilityNotificationsAllowed}
            onChange={(event) => setTextAvailabilityNotificationsAllowed(event.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-500 bg-scale-darker text-scale-blue focus:ring-scale-blue"
          />
          <span className="text-sm leading-6 text-gray-300">
            Text me at {client.phone} when a service I request becomes available. Message and data rates may apply. You can opt out at any time.
          </span>
        </label>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        {SERVICE_TYPES.map((type) => {
          const Icon = serviceTypeIcons[type.id as keyof typeof serviceTypeIcons];
          const selected = isTypeSelected(type.id);
          const availabilityUnknown = availabilityState !== 'ready';
          const unavailable = availabilityState === 'ready' && serviceIsUnavailable(availabilityRows, type.id);

          return (
            <button
              type="button"
              key={type.id}
              onClick={() => unavailable ? void handleUnavailableClick(type) : handleAvailableClick(type)}
              disabled={availabilityUnknown || notifyBusy === type.id}
              aria-pressed={selected}
              className={`relative group rounded-xl p-6 text-left transition-all duration-300 bg-scale-darker/50 backdrop-blur-sm border ${
                availabilityUnknown
                  ? 'cursor-not-allowed border-gray-800 opacity-60'
                  : unavailable
                  ? 'border-gray-800 opacity-60 hover:border-gray-600'
                  : selected
                  ? 'border-scale-blue ring-2 ring-scale-blue ring-opacity-50'
                  : 'border-gray-700 hover:border-scale-blue'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  unavailable
                    ? 'bg-gray-800 text-gray-500'
                    : selected
                    ? 'bg-scale-blue text-scale-dark'
                    : 'bg-gray-700 text-gray-300 group-hover:bg-scale-blue group-hover:text-scale-dark'
                } transition-colors`}>
                  <Icon className="w-6 h-6" />
                </div>
                {selected && !unavailable && (
                  <CheckCircle className="w-6 h-6 text-scale-blue flex-shrink-0" />
                )}
              </div>
              
              <h3 className="text-xl font-semibold text-white mb-2">{type.name}</h3>
              <p className="text-gray-300 text-sm mb-6">{type.description}</p>
              {type.id === CUSTOMER_INTELLIGENCE_SERVICE_TYPE && !unavailable && (
                <div className="mb-4 rounded-md border border-yellow-600/50 bg-yellow-600/10 px-3 py-2 text-xs leading-relaxed text-yellow-300">
                  Requires active Repeat & Referral. Selecting this also selects Repeat & Referral.
                </div>
              )}
              {(unavailable || availabilityUnknown) && (
                <div className="mb-4 rounded-md border border-gray-700 bg-black/20 px-3 py-2 text-xs text-gray-300">
                  <div className="mb-1 font-semibold text-gray-200">
                    {availabilityUnknown ? 'Availability unavailable' : 'Currently unavailable'}
                  </div>
                  {availabilityUnknown
                    ? 'Refresh the page before choosing this service.'
                    : notifyRequested[type.id]
                    ? 'Notification requested.'
                    : notifyBusy === type.id
                    ? 'Saving notification request...'
                    : serviceUnavailableMessage(availabilityRows, type.id)}
                </div>
              )}
              
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-white">Key Benefits:</h4>
                <ul className="space-y-2">
                  {type.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-300">
                      <ArrowRight className="w-4 h-4 text-scale-blue mr-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex justify-between items-center space-x-4 pt-4">
        <button
          onClick={handleBack}
          className="w-32 flex items-center justify-center px-6 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors font-semibold"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
        <div className="flex gap-3">
          {canSkipSteps() && (
            <button
              onClick={handleSkip}
              className="flex items-center bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors font-medium"
            >
              <FastForward className="w-4 h-4 mr-2" />
              Skip (Beta)
            </button>
          )}
          <button
            onClick={handleContinue}
            disabled={selectedServiceTypes.length === 0 || availabilityState !== 'ready'}
            className={`w-32 font-semibold px-6 py-2 rounded-md transition-colors ${
              selectedServiceTypes.length > 0 && availabilityState === 'ready'
                ? 'bg-scale-blue text-scale-dark hover:bg-white'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {selectedServiceTypes.length > 0 && (
        <div className="mt-6 p-4 bg-scale-darker/50 border border-gray-700 rounded-lg">
          <h4 className="text-sm font-semibold text-white mb-2">Selected Services:</h4>
          <div className="flex flex-wrap gap-2">
            {selectedServiceTypes.map((type) => (
              <div
                key={type.id}
                className="flex items-center bg-scale-blue/10 text-scale-blue px-3 py-1 rounded-full text-sm"
              >
                {type.name}
                <button
                  type="button"
                  aria-label={`Remove ${type.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAvailableClick(type);
                  }}
                  className="ml-2 hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
